import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { v4 as uuidv4 } from 'uuid';

import { Modal, useT } from '@repo/common';
import MobileLinkFullPage from './MobileLinkFullPage';

import { ReactComponent as DragAndDropIcon } from '@repo/common/assets/icons/16px/icon_16px_drag-and-drop.svg';
import { ReactComponent as DeleteIcon } from '@repo/common/assets/icons/24px/icon_24px_delete_outline.svg';
import { ReactComponent as PlusIcon } from '@repo/common/assets/icons/20px/icon_20px_plus_outline.svg';

const PLACEHOLDER_TEXT = 'Tap to add your link';

const EMPTY_ENTRY = { linkType: '', link: '' };

const LINK_TYPE_LABELS = {
  Link: '링크',
  Portfolio: '포트폴리오',
  'Personal Website': '개인 웹사이트',
  LinkedIn: 'LinkedIn',
  GitHub: 'GitHub',
  Behance: 'Behance',
  Dribbble: 'Dribbble',
  Notion: 'Notion',
  Blog: '블로그',
  Instagram: 'Instagram',
  TikTok: 'TikTok',
  Threads: 'Threads',
  'X (Twitter)': 'X (Twitter)',
  YouTube: 'YouTube',
};

const MobileLinkForm = ({ link, setResumeData }) => {
  const t = useT();
  const placeholderText = t(PLACEHOLDER_TEXT, '탭하여 링크를 추가하세요');
  const list = Array.isArray(link) ? link : [];

  const [editor, setEditor] = useState({ open: false, mode: 'add', id: null });
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  const commit = (next) => setResumeData((prev) => ({ ...prev, link: next }));

  const openAdd = () => setEditor({ open: true, mode: 'add', id: null });
  const openEdit = (id) => setEditor({ open: true, mode: 'edit', id });
  const closeEditor = () => setEditor({ open: false, mode: 'add', id: null });

  const addRow = () => commit([...list, { id: uuidv4(), ...EMPTY_ENTRY }]);

  const handleSave = (data) => {
    if (editor.mode === 'add') {
      commit([...list, { id: uuidv4(), ...data }]);
    } else {
      commit(list.map((e) => (e.id === editor.id ? { ...e, ...data } : e)));
    }
    closeEditor();
  };

  const handleConfirmDelete = () => {
    commit(list.filter((e) => e.id !== deleteTargetId));
    setDeleteTargetId(null);
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(list);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    commit(items);
  };

  const editingEntry =
    editor.mode === 'edit' ? list.find((e) => e.id === editor.id) : null;

  return (
    <div className='flex flex-col gap-3 w-full'>
      {list.length === 0 ? (
        <div className='flex items-center w-full'>
          <span className='shrink-0 text-grayscale-gray04 py-[14px] w-[20px]'>
            <DragAndDropIcon />
          </span>
          <button
            type='button'
            onClick={openAdd}
            className='flex-1 min-w-0 text-left rounded-[8px] border border-grayscale-gray02 bg-grayscale-gray00 p-3 typo-b14Strong text-text-placeholder'
          >
            {placeholderText}
          </button>
        </div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId='mobile-links' direction='vertical'>
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className='flex flex-col gap-3 w-full'
              >
                {list.map((lk, index) => {
                  const title = lk.linkType?.trim();
                  const displayTitle = title
                    ? t(title, LINK_TYPE_LABELS[title])
                    : '';

                  return (
                    <Draggable
                      key={lk.id}
                      draggableId={String(lk.id)}
                      index={index}
                    >
                      {(prov) => (
                        <div
                          ref={prov.innerRef}
                          {...prov.draggableProps}
                          className='flex items-center gap-2 w-full'
                        >
                          <span
                            {...prov.dragHandleProps}
                            className='shrink-0 cursor-grab active:cursor-grabbing text-grayscale-gray03'
                          >
                            <DragAndDropIcon width={20} height={20} />
                          </span>

                          <div className='flex-1 min-w-0 flex items-center rounded-[8px] border border-grayscale-gray02 bg-grayscale-gray00'>
                            <button
                              type='button'
                              onClick={() => openEdit(lk.id)}
                              className='flex-1 min-w-0 flex flex-col gap-1 text-left px-4 py-3'
                            >
                              <p
                                className={`truncate typo-b14Strong ${
                                  title
                                    ? 'text-text-primary'
                                    : 'text-text-placeholder'
                                }`}
                              >
                                {displayTitle || placeholderText}
                              </p>
                              {lk.link?.trim() && (
                                <p className='truncate typo-b12 text-text-secondary'>
                                  {lk.link}
                                </p>
                              )}
                            </button>

                            <button
                              type='button'
                              onClick={() => setDeleteTargetId(lk.id)}
                              className='shrink-0 self-stretch flex items-center px-3 text-grayscale-gray04'
                              aria-label={t('Delete', '삭제')}
                            >
                              <DeleteIcon width={20} height={20} />
                            </button>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      <button
        type='button'
        onClick={addRow}
        className='flex items-center justify-center h-12 w-full rounded-[8px] bg-grayscale-gray01 text-grayscale-gray05 transition-colors active:bg-grayscale-gray02'
        aria-label={t('Add link', '링크 추가')}
      >
        <PlusIcon width={20} height={20} />
      </button>

      {editor.open && (
        <MobileLinkFullPage
          initialData={editingEntry}
          onClose={closeEditor}
          onSave={handleSave}
        />
      )}

      {deleteTargetId && (
        <Modal
          isOpen
          onClickLeftBtn={() => setDeleteTargetId(null)}
          onClickRightBtn={handleConfirmDelete}
          title={t('Delete this link entry?', '이 링크 항목을 삭제할까요?')}
          subText={t(
            'This action can’t be undone.',
            '삭제 후에는 되돌릴 수 없습니다.',
          )}
          buttonSize='xl'
          leftButtonLabel={t('cancel', '취소')}
          leftHideIcon={true}
          rightButtonLabel={t('Delete', '삭제')}
          rightHideIcon={true}
          type='warning'
        />
      )}
    </div>
  );
};

export default MobileLinkForm;
